import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deepThink, setDeepThink] = useState(false);
  const [showLogoutMessage, setShowLogoutMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Parse available models from environment variable
  const availableModels = process.env.REACT_APP_OLLAMA_MODELS 
    ? process.env.REACT_APP_OLLAMA_MODELS.split(',')
    : [];
  
  // Parse available languages from environment variable
  const availableLanguages = process.env.REACT_APP_TRANSLATE_LANGUAGES
    ? process.env.REACT_APP_TRANSLATE_LANGUAGES.split(',')
    : [];
  const [selectedLanguage, setSelectedLanguage] = useState(availableLanguages[0] || '');
  const [translate, setTranslate] = useState(false);

  // Set the first model as default
  const [selectedModel, setSelectedModel] = useState(availableModels[0] || '');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleLogout = (e) => {
    e.preventDefault();
    e.stopPropagation();
    logout();
    setShowLogoutMessage(true);
    setMessages([]);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      console.log('Sending query with params:', {
        question: userMessage,
        deepThink,
        selectedModel,
        apiUrl: process.env.REACT_APP_API_URL
      });

      const response = await apiService.sendQuery(userMessage, deepThink, selectedModel);
      console.log('Received response:', response);

      // Format the response with all available information
      let responseContent = `${response.answer}\n\n---\nModel: ${response.model_used}\nStatus: ${response.status}`;

      // If translate is ON, call the translation API
      if (translate && selectedLanguage) {
        try {
          const res = await fetch(process.env.REACT_APP_TRANSLATE_URL, {
            method: "POST",
            body: JSON.stringify({
              q: response.answer,
              source: "auto",
              target: selectedLanguage,
              format: "text",
              alternatives: 3
            }),
            headers: { "Content-Type": "application/json" }
          });
          if (res.ok) {
            const data = await res.json();
            // Assume the translated text is in data.translated or similar
            const translated = data.translatedText || data.translated || data.translation || data.result || JSON.stringify(data);
            responseContent += `\n\n---\nTranslated (${selectedLanguage}):\n${translated}`;
          } else {
            responseContent += `\n\n---\n[Translation failed: ${res.status}]`;
          }
        } catch (err) {
          responseContent += `\n\n---\n[Translation error: ${err.message}]`;
        }
      }

      setMessages(prev => [...prev, { role: 'assistant', content: responseContent }]);
    } catch (error) {
      console.error('Chat error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Error: ${error.response?.data?.message || error.message || 'Sorry, I encountered an error. Please try again.'}` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {isAuthenticated ? (
          <Tooltip title="Logout">
            <IconButton 
              onClick={handleLogout}
              color="primary"
              sx={{ 
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={handleLogin}
            sx={{ 
              backgroundColor: 'white',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Login
          </Button>
        )}
      </Box>

      {showLogoutMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setShowLogoutMessage(false)}
        >
          Successfully logged out
        </Alert>
      )}

      {isAuthenticated ? (
        <>
          <Paper
            elevation={3}
            sx={{
              flexGrow: 1,
              mb: 2,
              p: 2,
              overflow: 'auto',
              backgroundColor: '#f5f5f5',
            }}
          >
            <List>
              {messages.map((message, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        maxWidth: '70%',
                        backgroundColor: message.role === 'user' ? '#e3f2fd' : 'white',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography 
                            variant="body1" 
                            component="div"
                            sx={{ whiteSpace: 'pre-wrap' }}
                          >
                            {message.content}
                          </Typography>
                        }
                      />
                    </Paper>
                  </ListItem>
                  {index < messages.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {isLoading && (
                <ListItem sx={{ justifyContent: 'flex-start' }}>
                  <CircularProgress size={20} />
                </ListItem>
              )}
              <div ref={messagesEndRef} />
            </List>
          </Paper>

          <Paper
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Ask a question..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={isLoading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              <Button
                type="submit"
                variant="contained"
                endIcon={<SendIcon />}
                disabled={isLoading || !input.trim()}
              >
                Send
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Tooltip title="Enable deep thinking mode for more thorough responses">
                <FormControlLabel
                  control={
                    <Switch
                      checked={deepThink}
                      onChange={(e) => setDeepThink(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PsychologyIcon fontSize="small" />
                      <Typography variant="body2">Deep Think</Typography>
                    </Box>
                  }
                />
              </Tooltip>
              {/* Translate Switch and Language Select */}
              <Tooltip title="Enable translation of responses">
                <FormControlLabel
                  control={
                    <Switch
                      checked={translate}
                      onChange={(e) => setTranslate(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">Translate</Typography>
                    </Box>
                  }
                  sx={{ ml: 2 }}
                />
              </Tooltip>
              <FormControl sx={{ minWidth: 120, ml: 2 }} disabled={!translate}>
                <InputLabel id="language-select-label">Language</InputLabel>
                <Select
                  labelId="language-select-label"
                  value={selectedLanguage}
                  label="Language"
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                >
                  {availableLanguages.map((lang) => (
                    <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* End Translate Switch and Language Select */}
              <FormControl sx={{ minWidth: 200, ml: 2 }}>
                <InputLabel id="model-select-label">Model</InputLabel>
                <Select
                  labelId="model-select-label"
                  value={selectedModel}
                  label="Model"
                  onChange={(e) => setSelectedModel(e.target.value)}
                  startAdornment={
                    <SmartToyIcon sx={{ mr: 1, color: 'action.active' }} />
                  }
                >
                  {availableModels.map((model) => (
                    <MenuItem key={model} value={model}>
                      {model}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </>
      ) : (
        <Box 
          sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 1
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Please log in to start chatting
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Chat; 